import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import Alert from 'reactstrap/lib/Alert';

import { receiveProduct, receiveProducts, receiveTickets } from '@ttstr/actions';
import {
  Container,
  LoadingSpinner,
  Masonry,
  useIntl,
  ContentrArea,
  VariantChooser,
  Maki,
  Maki2020,
} from '@ttstr/components';
import { AppState } from '@ttstr/reducers';
import { useActions, useMounted, useShallowEqualSelector } from '@ttstr/utils';
import { Article } from '@ttstr/api';
import { getProductById } from '@ttstr/reducers/productDetails';
import FilteredMasonry from './FilteredMasonry';

// wird nicht mehr gebraucht: Header soll ganz raus
// import MerchstandImages from './assets/images/MerchstandImages';

interface RouteParams {
  readonly id: string;
}

type Props = {
  /**
   * productFilter can be used if you want a Route to a dedicated page for special merch/products.
   *
   * Example:
   * You want a page only with merch that has the tag id 1234.
   * The function for productFilter would then be
   *  p => p.tag_ids.includes(1234)
   *
   * Then create a route in App.tsx with
   * <Route exact path="/specialmerch" render={()=> <ProductListing productFilter={p => p.tag_ids.includes(1234)} />} />
   */
  productFilter?: (p: Article) => any;
};

const Home: React.FC<Props> = ({ productFilter = (p) => p }) => {
  const { t } = useTranslation();
  const params = useParams<RouteParams>();
  const isMounted = useMounted();
  const [initialized, setInitialized] = React.useState(false);
  const {
    products,
    loadingProducts,
    tickets,
    loadingTickets,
    product,
    loadingProduct,
    secretProductId,
  } = useShallowEqualSelector(mapStateToProps);
  const { receiveProducts, receiveTickets, receiveProduct } = useActions(mapDispatchToProps);
  const { language } = useIntl();

  React.useEffect(() => {
    (async () => {
      try {
        setInitialized(false);
        await Promise.all([receiveProduct(secretProductId), receiveProducts(), receiveTickets()]);
      } finally {
        if (isMounted.current) setInitialized(true);
      }
    })();
  }, [language]);

  if (!productFilter && params?.id) {
    productFilter = (p) => p.category_ids.includes(Number(params.id));
  }

  return (
    <article className="mb-5">
      <Helmet>
        <title>{t(`PRODUCTS.TITLE`)}</title>
        <body className="landing-page" />
      </Helmet>

      <div className="list-page-highlights pt-2">
        <ContentrArea id="before-home-listing" />
        {/* <div className="list-page-highlights-header text-center">
          <img src={require('./assets/images/sehrmutigesbundle.png')} alt="" className="img-fluid" />
        </div> */}
        {/* <h2 className="highlight-title text-center">DAS SEHR MUTIGE BUNDLE</h2>
        <h5 className="text-center">
          Dieses Album-Bundle ist streng limitiert.
          <br />
          Alles Weitere musst du herausfinden.
          <br />
          Bist du mutig genug?
        </h5> */}
        <div className="container list-page">
          {loadingProducts || loadingTickets ? ( // if loading
            <LoadingSpinner label={t(`LOADING.PRODUCTS`)} />
          ) : products.length === 0 ? ( // if no products
            <Alert color="info">{t(`MASONRY.NO_ITEMS`)}</Alert>
          ) : (
            // else show products
            <FilteredMasonry
              id="product-listing"
              products={products.filter(productFilter)}
              filters="off"
              dontUpdateQueryStrings
              tagId={26}
              type={Maki}
              showSubtitle
              columnClassName="col-12 col-md-6 col-lg-6"
              className="justify-content-center"
              // itemClassName="random-rotation-object"
              // itemImageClassName="scribble-hover-overlay-object"
            />
          )}
        </div>
      </div>

      {/* <Container className="list-page-products my-5">
        <h1 className="superhead">Merchandise</h1>
        {loading ? ( // if loading
          <LoadingSpinner label={t(`LOADING.PRODUCTS`)} />
        ) : products.length === 0 ? ( // if no products
          <Alert color="info">{t(`MASONRY.NO_ITEMS`)}</Alert>
        ) : (
          // else show products
          <Masonry
            id="product-listing"
            products={products}
            columnClassName="col-sm-12 col-md-6 col-lg-4"
            filters="off"
            showFormerPrice
            showSubtitle
          />
        )}
      </Container> */}

      <Container className="ticket-listing">
        <h2 className="superhead text-center mb-5 mt-5">Tickets</h2>
        <ContentrArea id="before-ticket-listing" />
        {loadingProducts ? ( // if loading
          <LoadingSpinner label={t(`LOADING.PRODUCTS`)} />
        ) : products.length === 0 ? ( // if no products
          <Alert color="info">{t(`MASONRY.NO_ITEMS`)}</Alert>
        ) : (
          // else show products
          <Masonry
            id="ticket-listing"
            products={tickets}
            columnClassName="col-12"
            filters="off"
            type="list"
            showCity
            showLocation
          />
        )}
      </Container>
    </article>
  );
};

const mapStateToProps = (state: AppState) => {
  const secretProductId = 516;
  const { products } = state.Products;
  const loadingProducts = state.Products.loading;
  const { tickets } = state.Tickets;
  const loadingTickets = state.Tickets.loading;
  const loadingProduct = state.ProductDetails.loading;
  const product = getProductById(state, secretProductId);
  return {
    products,
    loadingProducts,
    tickets,
    loadingTickets,
    product,
    loadingProduct,
    secretProductId,
  };
};

const mapDispatchToProps = {
  receiveProducts,
  receiveTickets,
  receiveProduct,
};

export default React.memo(Home);
